import { useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { NavLink } from "react-router-dom";
import { InAppTemplate } from "../../../shared/templates/inapp";
import { storeActions } from "../../../redux/actions/in-app";
import { useToast } from "../../../shared/context/ToastContext";
import "../../../scss/packaging.scss";

type PackagingForm = {
  packagingOptionsEnabled: boolean;
  allowCustomerOwnBag: boolean;
  standardPackagingFee: string | number;
  standardPackagingDescription: string;
  premiumPackagingEnabled: boolean;
  premiumPackagingFee: string | number;
  premiumPackagingDescription: string;
  ecoPackagingEnabled: boolean;
  ecoPackagingFee: string | number;
  ecoPackagingDescription: string;
  giftWrappingEnabled: boolean;
  giftWrappingFee: string | number;
  giftWrappingDescription: string;
};

const emptyForm: PackagingForm = {
  packagingOptionsEnabled: false,
  allowCustomerOwnBag: false,
  standardPackagingFee: "",
  standardPackagingDescription: "",
  premiumPackagingEnabled: false,
  premiumPackagingFee: "",
  premiumPackagingDescription: "",
  ecoPackagingEnabled: false,
  ecoPackagingFee: "",
  ecoPackagingDescription: "",
  giftWrappingEnabled: false,
  giftWrappingFee: "",
  giftWrappingDescription: "",
};

const optionFields = [
  { key: "standard", title: "Standard packaging", enabled: null, fee: "standardPackagingFee", description: "standardPackagingDescription" },
  { key: "premium", title: "Premium packaging", enabled: "premiumPackagingEnabled", fee: "premiumPackagingFee", description: "premiumPackagingDescription" },
  { key: "eco", title: "Eco-friendly packaging", enabled: "ecoPackagingEnabled", fee: "ecoPackagingFee", description: "ecoPackagingDescription" },
  { key: "gift", title: "Gift wrapping", enabled: "giftWrappingEnabled", fee: "giftWrappingFee", description: "giftWrappingDescription" },
] as const;

const formatFee = (value: string | number) => {
  const normalizedValue = String(value ?? "").replace(/,/g, "");
  if (normalizedValue === "" || normalizedValue === ".") return normalizedValue;
  const [integerPart, ...decimalParts] = normalizedValue.split(".");
  const formattedInteger = (integerPart || "0").replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  return decimalParts.length ? `${formattedInteger}.${decimalParts.join("").slice(0, 2)}` : formattedInteger;
};

const getValidationErrors = (error: any): Record<string, string> => {
  const validation = error?.errors || error?.response?.data?.errors || error?.data?.errors;
  if (!validation || typeof validation !== "object") return {};
  return Object.keys(validation).reduce((messages, key) => {
    const message = validation[key];
    messages[key.toLowerCase()] = Array.isArray(message) ? message.join(" ") : String(message);
    return messages;
  }, {} as Record<string, string>);
};

const PackagingContent = ({ pageProps }: any) => {
  const { showToast } = useToast();
  const account = useMemo(() => {
    try { return JSON.parse(localStorage.getItem("mamiVendorXAuth") || "{}"); }
    catch { return {}; }
  }, []);
  const storeId = account?.selectedStore?.id;
  const [form, setForm] = useState<PackagingForm>(emptyForm);
  const [loaded, setLoaded] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [saved, setSaved] = useState(false);
  const [initialForm, setInitialForm] = useState<PackagingForm>(emptyForm);

  useEffect(() => {
    if (!storeId) return;
    setLoaded(false);
    setDirty(false);
    pageProps.getStorePackaging(storeId);
    pageProps.resetUpdateStorePackaging();
  }, [storeId]);

  useEffect(() => {
    if (pageProps.getPackagingRequest?.request_status !== "GET_STORE_PACKAGING_SUCCESS") return;
    const data = pageProps.getPackagingRequest.response || {};
    const loadedForm = { ...emptyForm, ...data };
    setForm(loadedForm);
    setInitialForm(loadedForm);
    setLoaded(true);
  }, [pageProps.getPackagingRequest?.request_status, pageProps.getPackagingRequest?.response]);

  useEffect(() => {
    if (pageProps.updatePackagingRequest?.request_status === "UPDATE_STORE_PACKAGING_SUCCESS") {
      setSaved(true);
      setDirty(false);
      showToast({
        title: "Packaging settings saved",
        description: "Your store's packaging options have been updated.",
        type: "success",
      });
    }
  }, [pageProps.updatePackagingRequest?.request_status, showToast]);

  const change = (key: keyof PackagingForm, value: any) => {
    setForm((current) => ({ ...current, [key]: value }));
    setDirty(true);
    setSaved(false);
  };

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    if (!storeId) return;
    const payload = {
      ...form,
      packagingOptionsEnabled: form.packagingOptionsEnabled,
      allowCustomerOwnBag: form.allowCustomerOwnBag,
      standardPackagingFee: Number(String(form.standardPackagingFee || 0).replace(/,/g, "")),
      premiumPackagingFee: Number(String(form.premiumPackagingFee || 0).replace(/,/g, "")),
      ecoPackagingFee: Number(String(form.ecoPackagingFee || 0).replace(/,/g, "")),
      giftWrappingFee: Number(String(form.giftWrappingFee || 0).replace(/,/g, "")),
    };
    pageProps.updateStorePackaging(storeId, payload);
  };

  const loadError = pageProps.getPackagingRequest?.request_status === "GET_STORE_PACKAGING_FAILURE";
  const saveError = pageProps.updatePackagingRequest?.request_status === "UPDATE_STORE_PACKAGING_FAILURE";
  const saving = pageProps.updatePackagingRequest?.request_status === "UPDATE_STORE_PACKAGING_PENDING";
  const saveValidationErrors = getValidationErrors(pageProps.updatePackagingRequest?.error || pageProps.updatePackagingRequest?.request_data?.error);

  return <div className="inapp-content">
    <div className="page-content cards setting-page packaging-settings">
      <div className="page-heading"><h3>Settings</h3></div>
      <div className="page-menus">
        <NavLink end className={({ isActive }) => isActive ? "active-nav" : ""} to="/app/profile/store">Store Details</NavLink>
        <NavLink end className={({ isActive }) => isActive ? "active-nav" : ""} to="/app/profile/staff">Staff Account</NavLink>
        <NavLink end className={({ isActive }) => isActive ? "active-nav" : ""} to="/app/profile/withdrawal">Withdrawal Method</NavLink>
        <NavLink end className={({ isActive }) => isActive ? "active-nav" : ""} to="/app/profile/packaging">Packaging</NavLink>
      </div>
      <div className="packaging-settings__body">
        <div className="packaging-settings__intro">
          <h4>Packaging options</h4>
          <p>Configure the packaging choices available to customers at your store.</p>
        </div>
        {!storeId && <div className="packaging-settings__message" role="alert">Select a store to manage its packaging settings.</div>}
        {storeId && !loaded && !loadError && <div className="packaging-settings__message">Loading packaging settings…</div>}
        {loadError && <div className="packaging-settings__message packaging-settings__message--error" role="alert">Could not load packaging settings. Please refresh the page to try again.</div>}
        {loaded && <form onSubmit={save}>
          <label className="packaging-settings__master-toggle">
            <input type="checkbox" checked={form.packagingOptionsEnabled} onChange={(event) => change("packagingOptionsEnabled", event.target.checked)} />
            <span><strong>Enable packaging options</strong><small>Allow customers to select packaging for orders from this store.</small></span>
          </label>
          <label className={`packaging-settings__own-bag ${!form.packagingOptionsEnabled ? "is-disabled" : ""}`}>
            <input type="checkbox" checked={form.allowCustomerOwnBag} disabled={!form.packagingOptionsEnabled} onChange={(event) => change("allowCustomerOwnBag", event.target.checked)} />
            <span>Allow customers to bring their own bag</span>
          </label>
          <h5 className="packaging-settings__section-title">Available packaging</h5>
          <div className="packaging-settings__options">
            {optionFields.map((option) => {
              const enabled = option.enabled ? Boolean(form[option.enabled]) : true;
              const disabled = !form.packagingOptionsEnabled || !enabled;
              return <section className={`packaging-settings__option ${disabled ? "is-disabled" : ""}`} key={option.key}>
                <div className="packaging-settings__option-heading">
                  <h6>{option.title}</h6>
                  {option.enabled && <label className="packaging-settings__switch"><input type="checkbox" checked={enabled} disabled={!form.packagingOptionsEnabled} onChange={(event) => change(option.enabled as keyof PackagingForm, event.target.checked)} /><span>{enabled ? "Enabled" : "Disabled"}</span></label>}
                </div>
                <div className="packaging-settings__fields">
                  <label>Description<input type="text" value={form[option.description]} disabled={disabled} maxLength={160} onChange={(event) => change(option.description, event.target.value)} placeholder="Enter a customer-facing description" /></label>
                  <label>Fee (CAD $)<input type="text" inputMode="decimal" value={formatFee(form[option.fee])} disabled={disabled} aria-invalid={Boolean(saveValidationErrors[option.fee.toLowerCase()]) || Number(String(form[option.fee]).replace(/,/g, "")) > 1000} aria-describedby={`${option.key}-fee-help`} onChange={(event) => {
                    const inputValue = event.target.value.replace(/,/g, "");
                    if (/^\d*(\.\d{0,2})?$/.test(inputValue) && (inputValue === "" || Number(inputValue) <= 1000)) {
                      change(option.fee, inputValue);
                    }
                  }} placeholder="0.00" />{Number(String(form[option.fee]).replace(/,/g, "")) > 1000 && <small className="packaging-settings__field-error" id={`${option.key}-fee-help`} role="alert">Fee must be between CAD $0.00 and CAD $1,000.00.</small>}{!saveValidationErrors[option.fee.toLowerCase()] && Number(String(form[option.fee]).replace(/,/g, "")) <= 1000 && <small id={`${option.key}-fee-help`}>Maximum fee: CAD $1,000.00</small>}{saveValidationErrors[option.fee.toLowerCase()] && <small className="packaging-settings__field-error" id={`${option.key}-fee-help`} role="alert">{saveValidationErrors[option.fee.toLowerCase()]}</small>}</label>
                </div>
              </section>;
            })}
          </div>
          {saveError && <div className="packaging-settings__message packaging-settings__message--error" role="alert">Could not save packaging settings. Check your connection and try again.</div>}
          <div className="packaging-settings__actions">
            <button type="button" className="packaging-settings__cancel" disabled={!dirty || saving} onClick={() => { setForm({ ...initialForm }); setDirty(false); setSaved(false); }}>Cancel</button>
            <button type="submit" className="packaging-settings__save" disabled={!dirty || saving}>{saving ? "Saving…" : "Save changes"}</button>
          </div>
        </form>}
      </div>
    </div>
  </div>;
};

const PackagingSettings = (pageProps: any) => <InAppTemplate pageHeadingTitle="Packaging Settings" childComponent={<PackagingContent pageProps={pageProps} />} />;

const mapDispatchToProps = {
  getStorePackaging: storeActions.GetStorePackaging,
  updateStorePackaging: storeActions.UpdateStorePackaging,
  resetUpdateStorePackaging: () => ({ type: "UPDATE_STORE_PACKAGING_RESET" }),
};

const mapStateToProps = (state: any) => ({
  getPackagingRequest: state.allProductReducers.getStorePackagingReducer,
  updatePackagingRequest: state.allProductReducers.updateStorePackagingReducer,
});

export default connect(mapStateToProps, mapDispatchToProps)(PackagingSettings);
